import { useState } from 'react'
import styled from 'styled-components'
import { motion } from 'framer-motion'
import { Calendar, Mail, Phone, MapPin, Send } from 'lucide-react'

const PageHeader = styled.section`
  padding: calc(var(--header-height) + 64px) 24px 48px;
  text-align: center;
  position: relative;
`

const HeaderGlow = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 50% 0%, var(--color-accent-glow) 0%, transparent 60%);
  pointer-events: none;
`

const HeaderContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  margin: 0 auto;
`

const Eyebrow = styled.p`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--color-accent);
  margin-bottom: 16px;
`

const PageTitle = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  margin-bottom: 20px;
  line-height: 1.15;
`

const PageSubtitle = styled.p`
  font-size: 1.15rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
`

const ContentSection = styled.section`
  padding: 32px 24px 96px;
`

const ContentInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 600px;
  }
`

const CalendlySection = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 32px;
`

const SectionTitle = styled.h2`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: var(--color-accent);
  }
`

const CalendlyDesc = styled.p`
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  line-height: 1.7;
  margin-bottom: 24px;
`

const CalendlyButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 14px 32px;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 1rem;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.9;
    transform: translateY(-2px);
    color: white;
  }
`

const ContactForm = styled(motion.form)`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 32px;
`

const FormGroup = styled.div`
  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-bottom: 6px;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--color-accent);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  background: var(--color-bg-elevated);
  color: var(--color-text);
  font-family: var(--font-family);
  font-size: 0.95rem;
  outline: none;
  min-height: 120px;
  resize: vertical;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: var(--color-accent);
  }

  &::placeholder {
    color: var(--color-text-muted);
  }
`

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px 24px;
  background: linear-gradient(135deg, var(--color-gradient-start), var(--color-gradient-end));
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.9;
  }
`

const SuccessMessage = styled(motion.div)`
  text-align: center;
  padding: 32px;

  h3 {
    color: var(--color-success);
    font-size: 1.2rem;
    margin-bottom: 8px;
  }

  p {
    color: var(--color-text-secondary);
    font-size: 0.95rem;
  }
`

const InfoSection = styled.section`
  padding: 0 24px 96px;
`

const InfoInner = styled.div`
  max-width: var(--max-width);
  margin: 0 auto;
`

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 400px;
    margin: 0 auto;
  }
`

const InfoCard = styled(motion.div)`
  background: var(--color-bg-card);
  border: 1px solid var(--color-border);
  border-radius: 16px;
  padding: 28px;
  text-align: center;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: var(--color-accent);
  }
`

const InfoIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--color-accent-glow);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
  margin: 0 auto 16px;
`

const InfoLabel = styled.h3`
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 6px;
`

const InfoValue = styled.a`
  font-size: 0.9rem;
  color: var(--color-text-secondary);
  transition: color 0.2s ease;

  &:hover {
    color: var(--color-accent);
  }
`

export default function Contact() {
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = (e) => {
    e.preventDefault()
    setSubmitted(true)
  }

  return (
    <>
      <PageHeader>
        <HeaderGlow />
        <HeaderContent>
          <Eyebrow>Contact</Eyebrow>
          <PageTitle>Let's talk about your business</PageTitle>
          <PageSubtitle>
            Book a free discovery call or send us a message. No pitch, no pressure;
            just a conversation about what's possible.
          </PageSubtitle>
        </HeaderContent>
      </PageHeader>

      <ContentSection>
        <ContentInner>
          <CalendlySection
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <SectionTitle>
              <Calendar size={22} /> Book a Discovery Call
            </SectionTitle>
            <CalendlyDesc>
              Free 30-minute call. We'll learn about your business, identify where AI and
              automation can make the biggest impact, and give you an honest recommendation.
            </CalendlyDesc>
            <CalendlyButton
              href="https://calendly.com/coybyron/30min"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Calendar size={18} /> Schedule on Calendly
            </CalendlyButton>
          </CalendlySection>

          <ContactForm
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            onSubmit={handleSubmit}
          >
            <SectionTitle>
              <Mail size={22} /> Send a Message
            </SectionTitle>
            {submitted ? (
              <SuccessMessage initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h3>Message sent</h3>
                <p>We'll get back to you within 24 hours.</p>
              </SuccessMessage>
            ) : (
              <>
                <FormGroup>
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" type="text" placeholder="Your name" required />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" placeholder="your@email.com" required />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="business">Business</Label>
                  <Input id="business" type="text" placeholder="Your company name" />
                </FormGroup>
                <FormGroup>
                  <Label htmlFor="message">What do you need help with?</Label>
                  <TextArea id="message" placeholder="Tell us about your business and what you're looking for..." required />
                </FormGroup>
                <SubmitButton type="submit">
                  Send Message <Send size={16} />
                </SubmitButton>
              </>
            )}
          </ContactForm>
        </ContentInner>
      </ContentSection>

      <InfoSection>
        <InfoInner>
          <InfoGrid>
            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
            >
              <InfoIcon><Mail size={22} /></InfoIcon>
              <InfoLabel>Email</InfoLabel>
              <InfoValue href="mailto:contact@valyss.com">contact@valyss.com</InfoValue>
            </InfoCard>
            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <InfoIcon><Phone size={22} /></InfoIcon>
              <InfoLabel>Phone</InfoLabel>
              <InfoValue href="tel:+16464705147">(646) 470-5147</InfoValue>
            </InfoCard>
            <InfoCard
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <InfoIcon><MapPin size={22} /></InfoIcon>
              <InfoLabel>Location</InfoLabel>
              <InfoValue as="p">Silicon Valley, CA</InfoValue>
            </InfoCard>
          </InfoGrid>
        </InfoInner>
      </InfoSection>
    </>
  )
}
